import { ArrowUpRight, Landmark, Sparkles, Unlock } from 'lucide-react';
import type { ProjectedMatrixCycle, ProjectedSlotView } from '../lib/lookup-types';
import { PositionGrid, PositionLegend } from './PositionGrid';
import { atomicToDecimal, useAppData } from '../state/AppDataContext';
import { formatToken } from '../lib/format';
import { SUB_SLOTS, nextSlot, slotLabel, slotTitle } from '../lib/slots';

/*
 * Shared slot / cycle / position presentation (rules/rule7.txt section 4 and addendum A):
 *  - every activated slot is clickable the moment it opens, even with all twelve seats empty;
 *  - every opened cycle has an explicit "Cycle N" tab, always visible, even when there is only one;
 *  - every position of the selected cycle is drawn, filled or open.
 * Used by the public lookup, the admin console (explorer + ID browser cards) and the member's own
 * slot pages so the three surfaces never disagree.
 */

export const SLOT_NUMBERS: readonly number[] = Array.from({ length: 16 }, (_, index) => index + 1);
/** Every slot key in progression order: Slot ¼ (25), Slot ½ (50), then 1 .. 16 (Half / Quarter era). */
export const ALL_SLOT_KEYS: readonly number[] = [...SUB_SLOTS, ...SLOT_NUMBERS];
export const POSITION_NUMBERS: readonly number[] = Array.from({ length: 12 }, (_, index) => index + 1);

/** Distinct filled seats in one cycle (a position may carry two events, e.g. routing + treasury). */
export function filledPositions(cycle: ProjectedMatrixCycle): number {
  return new Set(cycle.positions.filter((item) => item.occupantId).map((item) => item.position)).size;
}

export function SlotTrack({
  activeSlots,
  virtualSlots = [],
  selected,
  onSelect,
  label,
}: {
  /** Slots the contract has activated for this ID (indexed activations / on-chain slot views) - nothing else. */
  activeSlots: readonly number[];
  /** Virtual sub-slots (Slot ½ / ¼ owned only to seat lower-era IDs) - drawn with a "virtual" marker. */
  virtualSlots?: readonly number[];
  selected: number | null;
  onSelect(slotNumber: number): void;
  label: string;
}) {
  // Half / Quarter era: Slot ¼ / Slot ½ come BEFORE Slot 1, and only when this ID has them.
  const subSlots = SUB_SLOTS.filter((slot) => activeSlots.includes(slot));
  const track = [...subSlots, ...SLOT_NUMBERS];
  return (
    <div className={`journey-track neon-track${subSlots.length ? ` sub-${subSlots.length}` : ''}`} aria-label={label}>
      {track.map((slotNumber) => {
        const active = activeSlots.includes(slotNumber);
        const isVirtual = virtualSlots.includes(slotNumber);
        const name = slotTitle(slotNumber, isVirtual);
        const title = active
          ? `${name}: activated on-chain${isVirtual ? ' (seats lower-era IDs; no payment, no upgrade)' : ''}`
          : `${name}: not activated on-chain yet`;
        return (
          <button
            key={slotNumber}
            type="button"
            disabled={!active}
            className={`${active ? 'active' : ''}${isVirtual ? ' virtual' : ''}${selected === slotNumber ? ' selected' : ''}`}
            title={title}
            aria-label={name}
            aria-pressed={selected === slotNumber}
            onClick={() => active && onSelect(slotNumber)}
          >
            {slotLabel(slotNumber)}
          </button>
        );
      })}
    </div>
  );
}

/** Legend of the slot track: every highlighted slot is one the contract activated (owner 2026-10-11, on-chain only). */
export function TrackLegend({ children }: { children?: React.ReactNode }) {
  return (
    <div className="track-legend">
      <span className="onchain"><i />Activated on-chain</span>
      <span className="locked"><i />Not activated on-chain</span>
      {children}
    </div>
  );
}

export function CycleTabs({
  cycles,
  selected,
  onSelect,
  currentCycle,
}: {
  cycles: readonly number[];
  selected: number;
  onSelect(cycleNumber: number): void;
  currentCycle: number | null;
}) {
  return (
    <div className="cycle-tabs" role="tablist" aria-label="Cycles">
      {cycles.map((cycleNumber) => (
        <button
          key={cycleNumber}
          type="button"
          role="tab"
          aria-selected={selected === cycleNumber}
          className={`cycle-tab${selected === cycleNumber ? ' selected' : ''}${cycleNumber === currentCycle ? ' current' : ''}`}
          title={cycleNumber === currentCycle ? `Cycle ${cycleNumber} (current, still filling)` : `Cycle ${cycleNumber}`}
          onClick={() => onSelect(cycleNumber)}
        >
          Cycle {cycleNumber}
          {cycleNumber === currentCycle ? <span className="cycle-tab-live" aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}

/**
 * Where a slot grid's seats come from, labelled the same way on every surface:
 *  - 'onchain'       the API built the grid from the indexed on-chain seats (SeatFilled events) - every API since
 *                    2026-10-11 sends only these;
 *  - 'unconfirmed'   a view the API did not mark as on-chain: labelled as such, never presented as the contract's.
 */
export function slotGridSource(slot: ProjectedSlotView, slotActiveOnChain: boolean): { tone: 'onchain' | 'unconfirmed'; badge: string; short: string; title: string } {
  const name = `Slot ${slotLabel(slot.slotNumber)}`;
  if (slot.source === 'onchain') {
    return {
      tone: 'onchain',
      badge: slot.virtual ? 'Virtual slot · on-chain seats' : 'On-chain slot · on-chain seats',
      short: slot.virtual ? 'on-chain · virtual' : 'on-chain',
      title: slot.virtual
        ? `${name} is a VIRTUAL slot: it exists on-chain only to seat lower-era IDs (no entry payment, no upgrade, cycle-2+ table). Every seat shown is read from the contract's indexed SeatFilled events.`
        : `${name} is active on-chain and every seat shown is read from the contract's indexed SeatFilled events.`,
    };
  }
  if (slotActiveOnChain) {
    return {
      tone: 'unconfirmed',
      badge: 'On-chain slot · seats not confirmed',
      short: 'on-chain slot · seats not confirmed',
      title: `${name} is active on-chain, but the seats shown are not marked as the contract's indexed SeatFilled events.`,
    };
  }
  return {
    tone: 'unconfirmed',
    badge: 'Not activated on-chain',
    short: 'not activated on-chain',
    title: `${name} has not been activated by the contract; the seats shown are not confirmed by any SeatFilled event.`,
  };
}

/**
 * One slot of one member: status line, entry note (slot 2+), cycle tabs and the full position grid of the selected
 * cycle. `positions` narrows the grid to an admin selection. Every surface passes on-chain slot views (source
 * 'onchain', seats from SeatFilled); a view without that mark is labelled "not confirmed", never as the contract's.
 */
export function ProjectedSlotPanel({
  slot,
  onChain,
  selectedCycle,
  onSelectCycle,
  positions,
  columns = 6,
  showLegend = true,
  compact = false,
}: {
  slot: ProjectedSlotView;
  onChain: boolean;
  selectedCycle: number;
  onSelectCycle(cycleNumber: number): void;
  positions?: number[] | null | undefined;
  columns?: number;
  showLegend?: boolean;
  compact?: boolean;
}) {
  const { placementRules } = useAppData();
  const fallback: ProjectedMatrixCycle = { cycleNumber: 1, positions: [] };
  const cycle = slot.cycles.find((item) => item.cycleNumber === selectedCycle) ?? slot.cycles[slot.cycles.length - 1] ?? fallback;
  const filled = filledPositions(cycle);
  const source = slotGridSource(slot, onChain);
  const name = `slot ${slotLabel(slot.slotNumber)}`;
  const opened = nextSlot(slot.slotNumber);
  // Only an upgrade slot has a host / Treasury entry; the API leaves both empty for an entry slot (Slot 1, ½ or ¼)
  // and for a virtual sub-slot.
  const entryNote = slot.placedUnder
    ? `Entered under ${slot.placedUnder} (${slot.placedUnderLevelsUp === 1 ? 'direct sponsor' : `${slot.placedUnderLevelsUp ?? '?'} levels up`})`
    : slot.enteredViaTreasury
      ? `Entry went to Treasury (no eligible senior within ${placementRules.uplineSearchLimit} levels)`
      : null;

  return (
    <div className={`slot-matrix${compact ? ' compact' : ''}`}>
      <div className="slot-matrix-status">
        <span className={`slot-matrix-badge ${source.tone}`} title={source.title}>
          {source.tone === 'onchain' ? <Unlock size={11} /> : <Sparkles size={11} />} {source.badge}
        </span>
        {cycle.ownerEarnedAtomic !== undefined ? <span className="slot-matrix-earned" title="Sum of the SeatFilled amounts of the slot-owner seats (on-chain)">Owner seats value this cycle {formatToken(atomicToDecimal(cycle.ownerEarnedAtomic))} · this slot {formatToken(atomicToDecimal(slot.ownerEarnedTotalAtomic ?? '0'))}</span> : null}
        <span>Cycle {cycle.cycleNumber} · {filled}/12 filled{cycle.cycleNumber === slot.currentCycleNumber ? ` · next open P${slot.currentOpenPosition}` : ' · closed'}</span>
        {entryNote ? (
          <span className={`slot-matrix-entry${slot.enteredViaTreasury ? ' treasury' : ''}`}>
            {slot.enteredViaTreasury ? <Landmark size={12} /> : <ArrowUpRight size={12} />} {entryNote}
          </span>
        ) : null}
        {slot.virtual ? (
          <span className="slot-matrix-entry virtual-slot" title="A virtual slot seats lower-era IDs for this ID; it was never paid for and never upgrades (rule 16 Section I).">
            <Sparkles size={12} /> Virtual {name} · no upgrade
          </span>
        ) : null}
        {slot.nextSlotOpened && !slot.virtual && opened !== null ? (
          <span className="slot-matrix-entry next-slot" title={`Cycle-1 P4 of ${name} has filled, which opens slot ${slotLabel(opened)} for this ID (rule 7). You are still viewing ${name}.`}>
            <Sparkles size={12} /> P4 filled here, so this ID also has slot {slotLabel(opened)} open
          </span>
        ) : null}
      </div>
      <CycleTabs cycles={slot.cycles.map((item) => item.cycleNumber)} selected={cycle.cycleNumber} onSelect={onSelectCycle} currentCycle={slot.currentCycleNumber} />
      <PositionGrid cycle={cycle} columns={columns} positions={positions} />
      {slot.ownerEarnedTotalAtomic !== undefined ? (
        <div className="slot-earnings" aria-label={`Slot ${slotLabel(slot.slotNumber)} earnings by cycle`}>
          <div className="slot-earnings-row head" title="Owner seats and their SeatFilled amounts, as the contract emitted them"><span>Slot {slotLabel(slot.slotNumber)} cycle</span><span>Owner seats filled</span><span>Owner seats value (on-chain)</span></div>
          {slot.cycles.map((item) => (
            <button
              type="button"
              key={item.cycleNumber}
              className={`slot-earnings-row${item.cycleNumber === cycle.cycleNumber ? ' selected' : ''}`}
              onClick={() => onSelectCycle(item.cycleNumber)}
            >
              <span>Cycle {item.cycleNumber}{item.cycleNumber === slot.currentCycleNumber ? ' (running)' : ''}</span>
              <span>{item.ownerSeats ?? 0} of 5</span>
              <strong>{formatToken(atomicToDecimal(item.ownerEarnedAtomic ?? '0'))}</strong>
            </button>
          ))}
          <div className="slot-earnings-row total">
            <span>All cycles of slot {slotLabel(slot.slotNumber)}</span>
            <span>{slot.cycles.reduce((sum, item) => sum + (item.ownerSeats ?? 0), 0)} seats</span>
            <strong>{formatToken(atomicToDecimal(slot.ownerEarnedTotalAtomic))}</strong>
          </div>
        </div>
      ) : null}
      {showLegend ? <PositionLegend /> : null}
    </div>
  );
}
